<template lang="pug">
  div.sfu-wrapper(@click.prevent="onClick")
    template(v-if="hasFile")
      slot(:file="file"
            :progress="progress"
            :uploading="uploading"
            :success="success"
            :error="error"
            :errorMessage="errorMessage"
            :result="uploadResult")
        //template(v-if="hasFile")
        //  div File: {{file}}
        //template(v-if="!hasFile")
        //  div Waiting for file
</template>

<script lang="ts" setup>

import {computed, onMounted, ref} from "vue";

const props = defineProps({
  customUpload: {
    type: Boolean,
    default: false
  },
  meta: {
    type: Object,
    default: () => ({})
  },
  name: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  url: {
    type: String,
    default: null
  },
  withCredentials: {
    type: Boolean,
    default: false
  },
  file: {
    type: [Object, File, null],
    default: null
  },
  auto: {
    type: Boolean,
    default: true
  },
  accept: {
    type: String,
    default: null
  },
  invalidFileTypeMessage: {
    type: String,
    default: 'Invalid file type, allowed file types: {1}.'
  },
  maxFileSize: {
    type: Number,
    default: null
  },
  invalidFileSizeMessage: {
    type: String,
    default: 'Invalid file size, file size should be smaller than {1}.'
  },
})


const uploading = ref(false)
const uploadResult = ref(null)

const file = ref(null)
const messages = ref([])
const progress = ref(0)

const hasFile = computed(() => {
  return !!file.value;
})

onMounted(() => {
  file.value = props.file;
  if (props.auto && props.file)
  {
    // console.log(`auto upload ${props.file}`);
    upload();
  }
})

const onClick = () => {
  if (hasFile.value && !uploading.value && !uploadResult.value)
    upload()
}

const emits = defineEmits([
  'uploader', 'upload-start', 'clear',
  'before-upload', 'progress', 'upload', 'error',
  'before-send', 'upload-end'
])

const success = computed(() => {
  return hasFile.value && (
    (!uploading.value && uploadResult.value && uploadResult.value.success)
    && (messages.value.length == 0)
  );
})

const error = computed(() => {
  return hasFile.value
    && (
      (!uploading.value && uploadResult.value && !uploadResult.value.success)
      || (messages.value.length > 0)
    );
})

const clear = () => {
  messages.value.splice(0);
  emits('clear', {meta: props.meta});
}

const upload = () => {
  uploadResult.value = null;
  progress.value = 0;

  if (!validate(file.value))
    return;

  if (props.customUpload) {
    emits('uploader', {file: file.value, meta: props.meta});
    clear();
  } else {
    emits('upload-start', {file: file.value, meta: props.meta});
    uploading.value = true;

    let xhr = new XMLHttpRequest();
    let formData = new FormData();

    emits('before-upload', {
      'xhr': xhr,
      'formData': formData,
      meta: props.meta
    });

    formData.append(props.name, file.value, file.name);

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        progress.value = Math.min(99, Math.round((event.loaded * 100) / event.total));
      }

      emits('progress', {
        originalEvent: event,
        progress: progress.value,
        meta: props.meta
      });
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        progress.value = 0;
        emits('upload-end', {file: file.value, meta: props.meta});

        if (xhr.status >= 200 && xhr.status < 300) {
          uploadResult.value = {
            success: true,
            xhr: xhr,
            file: file.value,
            meta: props.meta
          }
          uploading.value = false
          emits('upload', {
            xhr: xhr,
            file: file.value,
            meta: props.meta
          });
        } else {
          uploadResult.value = {
            success: false,
            xhr: xhr,
            file: file.value,
            meta: props.meta
          }
          uploading.value = false

          emits('error', {
            xhr: xhr,
            file: file.value,
            meta: props.meta
          });
        }

        clear();
      }
    };

    xhr.open('POST', props.url, true);

    emits('before-send', {
      xhr: xhr,
      formData: formData,
      meta: props.meta
    });

    xhr.withCredentials = props.withCredentials;

    xhr.send(formData);
  }
}

const errorMessage = computed(() => {
  if (messages.value.length > 0)
    return messages.value[0];

  if (hasFile.value && !uploading.value && error.value) {
    const xhr = uploadResult.value.xhr;
    if (xhr.status == 422) {
      const response = JSON.parse(xhr.response)
      return response.message;
    } else {
      return xhr.status + ': ' + xhr.response;
    }
    return xhr.status + (xhr.statusText ? ': ' + xhr.statusText : '');
  } return null;
})

const uploadFile = (file) => {
  file.value = file;
  upload()
}

const acceptFormatText = computed(() => {
  if (props.accept) {
    return props.accept.split(',').map(mimetype => {
      const r = mimetype.trim().split('/').pop();
      return r.toUpperCase();
    }).filter((s) => !!s).join(', ');
  }
  else {
    return 'any';
  }
})

const validate = (file) => {
  messages.value.splice(0);

  if (props.accept && !isFileTypeValid(file)) {
    messages.value.push(props.invalidFileTypeMessage.replace('{0}', file.name).replace('{1}', acceptFormatText.value))
    return false;
  }

  if (props.maxFileSize && file.size > props.maxFileSize) {
    messages.value.push(props.invalidFileSizeMessage.replace('{0}', file.name).replace('{1}', formatSize(props.maxFileSize)));
    return false;
  }

  return true;
}

const isFileTypeValid = (file) => {
  let acceptableTypes = props.accept.split(',').map(type => type.trim());
  for (let type of acceptableTypes) {
    let acceptable = isWildcard(type) ? getTypeClass(file.type) === getTypeClass(type)
      : file.type == type || getFileExtension(file).toLowerCase() === type.toLowerCase();
    if (acceptable) {
      return true;
    }
  }
  return false;
}

const getFileExtension = (file) => {
  return '.' + file.name.split('.').pop();
}


const isWildcard = (fileType) => {
  return fileType.indexOf('*') !== -1;
}

const getTypeClass = (fileType) => {
  return fileType.substring(0, fileType.indexOf('/'));
}

const formatSize = (bytes) => {
  if (bytes === 0) {
    return '0 B';
  }
  let k = 1000,
    dm = 3,
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

defineExpose([
  uploadFile,
  hasFile
])
</script>

<style scoped lang="less">
.sfu-wrapper {
  display: contents;
}
</style>
